// Phase 4A — Charles client-tool dispatcher.
// Keyed EXACTLY on docs/phase4a.md §1 tool names. Each handler returns a
// ToolResult; never throws into the ConvAI widget.
//
// NOTE: Deployed RPC arg names use leading underscores (_org_id, _value, ...)
// per Phase 2/4 migrations. Spec text used "p_" — we honor the actual DB.

import { supabase } from "@/lib/supabase";
import type {
  AddTurnArgs,
  EndConversationArgs,
  RecordActionItemArgs,
  SessionContext,
  SubmitAnchorResponseArgs,
  SubmitPulseArgs,
  SubmitQuestionResponseArgs,
  ToolResult,
} from "./types";

function fail(code: string, message: string): ToolResult {
  return { ok: false, code, message };
}

export function createCharlesDispatcher(ctx: SessionContext) {
  const handlers = {
    submit_pulse: async (args: SubmitPulseArgs): Promise<ToolResult> => {
      if (import.meta.env.DEV) console.info(`[charles:tool] name=submit_pulse args=${JSON.stringify(args)}`);
      if (!Number.isInteger(args?.score) || args.score < 1 || args.score > 10) {
        return fail("invalid_args", "score must be an integer 1..10");
      }
      const { error } = await supabase.rpc("submit_pulse", {
        _org_id: ctx.orgId,
        _value: args.score,
      });
      return error ? fail("rpc_error", error.message) : { ok: true };
    },

    submit_question_response: async (
      args: SubmitQuestionResponseArgs,
    ): Promise<ToolResult> => {
      if (import.meta.env.DEV) console.info(`[charles:tool] name=submit_question_response args=${JSON.stringify(args)}`);
      if (!args?.question_id || !args?.schedule_id || !args?.response) {
        return fail(
          "invalid_args",
          "question_id, schedule_id, response required",
        );
      }
      const { error } = await supabase.rpc("submit_question_response", {
        _question_id: args.question_id,
        _schedule_id: args.schedule_id,
        _response: args.response,
      });
      return error ? fail("rpc_error", error.message) : { ok: true };
    },
    submit_anchor_response: async (
      args: SubmitAnchorResponseArgs,
    ): Promise<ToolResult> => {
      if (import.meta.env.DEV) console.info(`[charles:tool] name=submit_anchor_response args=${JSON.stringify(args)}`);
      if (
        !args?.survey_id ||
        !args?.anchor_id ||
        !Number.isInteger(args?.value) ||
        args.value < 1 ||
        args.value > 5
      ) {
        // eslint-disable-next-line no-console
        console.warn("[charles:tool] submit_anchor_response REJECTED (invalid_args)", {
          survey_id: args?.survey_id,
          anchor_id: args?.anchor_id,
          value: args?.value,
        });
        return fail(
          "invalid_args",
          "ERROR: survey_id, anchor_id, and value (1..5) required. You MUST copy survey_id and anchor_id verbatim from anchor_briefs[] for the active survey.",
        );
      }
      const { error } = await supabase.rpc("submit_anchor_response", {
        _org_id: ctx.orgId,
        _survey_id: args.survey_id,
        _anchor_id: args.anchor_id,
        _value: args.value,
      });
      if (error) {
        // eslint-disable-next-line no-console
        console.warn("[charles:tool] submit_anchor_response REJECTED by RPC", {
          survey_id: args.survey_id,
          anchor_id: args.anchor_id,
          value: args.value,
          rpc_error: error.message,
        });
        return fail(
          "rpc_error",
          `ERROR: ${error.message}. The survey_id/anchor_id you sent does not exist for this org's active survey. Copy them verbatim from anchor_briefs[] — never guess or reuse ids from recent_anchor_responses (those are historical).`,
        );
      }
      return { ok: true };
    },

    record_action_item: async (
      args: RecordActionItemArgs,
    ): Promise<ToolResult> => {
      if (import.meta.env.DEV) console.info(`[charles:tool] name=record_action_item args=${JSON.stringify(args)}`);
      if (args?.org_id && args.org_id !== ctx.orgId) {
        // eslint-disable-next-line no-console
        console.warn("[charles:tool] record_action_item IGNORING agent-supplied org_id", {
          supplied: args.org_id,
          ctx: ctx.orgId,
        });
      }
      const orgId = ctx.orgId;
      if (!orgId || !args?.title) {
        return fail("invalid_args", "ERROR: title required. Do not supply org_id — it is bound from session context.");
      }
      const { error } = await supabase.rpc("record_action_item", {
        _org_id: orgId,
        _title: args.title,
        _detail: args.detail ?? null,
      });
      if (error) {
        // eslint-disable-next-line no-console
        console.warn("[charles:tool] record_action_item REJECTED by RPC", { rpc_error: error.message });
        return fail("rpc_error", `ERROR: ${error.message}`);
      }
      return { ok: true };
    },

    end_conversation: async (
      args: EndConversationArgs,
    ): Promise<ToolResult> => {
      if (import.meta.env.DEV) console.info(`[charles:tool] name=end_conversation args=${JSON.stringify(args)}`);
      if (args?.conversation_id && args.conversation_id !== ctx.conversationId) {
        // eslint-disable-next-line no-console
        console.warn("[charles:tool] end_conversation IGNORING agent-supplied conversation_id", {
          supplied: args.conversation_id,
          ctx: ctx.conversationId,
        });
      }
      const id = ctx.conversationId;
      if (!id) return fail("invalid_args", "ERROR: no active conversation in session context");
      const { error } = await supabase.rpc("end_conversation", {
        _conversation_id: id,
      });
      if (error) {
        // eslint-disable-next-line no-console
        console.warn("[charles:tool] end_conversation REJECTED by RPC", { rpc_error: error.message });
        return fail("rpc_error", `ERROR: ${error.message}`);
      }
      return { ok: true };
    },

    add_turn: async (args: AddTurnArgs): Promise<ToolResult> => {
      if (import.meta.env.DEV) console.info(`[charles:tool] name=add_turn args=${JSON.stringify({ role: args?.role, len: args?.content?.length })}`);
      // A2 amendment 2: drop system turns silently (DB enum has no 'system').
      if (args?.role === "system") {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.warn("[charles] dropping system turn");
        }
        return { ok: true };
      }
      if (args?.conversation_id && args.conversation_id !== ctx.conversationId) {
        // eslint-disable-next-line no-console
        console.warn("[charles:tool] add_turn IGNORING agent-supplied conversation_id", {
          supplied: args.conversation_id,
          ctx: ctx.conversationId,
        });
      }
      const id = ctx.conversationId;
      if (!id || !args?.role || !args?.content) {
        return fail(
          "invalid_args",
          "ERROR: role and content required. Do not supply conversation_id — it is bound from session context.",
        );
      }
      const { error } = await supabase.rpc("add_turn", {
        _conversation_id: id,
        _role: args.role,
        _content: args.content,
      });
      if (error) {
        // eslint-disable-next-line no-console
        console.warn("[charles:tool] add_turn REJECTED by RPC", { rpc_error: error.message });
        return fail("rpc_error", `ERROR: ${error.message}`);
      }
      return { ok: true };
    },
  } as const;

  async function dispatch(name: string, params: unknown): Promise<ToolResult> {
    const h = (handlers as Record<string, (a: any) => Promise<ToolResult>>)[
      name
    ];
    if (!h) {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`[charles] unknown client tool: ${name}`);
      }
      return fail("unknown_tool", `unknown tool: ${name}`);
    }
    try {
      return await h(params);
    } catch (e) {
      return fail("handler_threw", e instanceof Error ? e.message : String(e));
    }
  }

  return { handlers, dispatch };
}

export type CharlesDispatcher = ReturnType<typeof createCharlesDispatcher>;
